/**
 * Group the server's accepted extensions into the families the upload
 * preflight dialog presents (`lexide-promptbox-preflight-and-level-miniplan.md`
 * §2.1: documentos / imagens / áudio / vídeo / arquivos compactados).
 *
 * ⚠ THE FAMILY IS A DISPLAY GROUPING, THE EXTENSION LIST IS NOT.
 * The list always comes from `acceptedExtensions(limits)` — the server's own
 * route-table-derived field. This module never decides WHAT is accepted, only
 * how to shelve it for a human. The distinction matters: the Acervo view was
 * built once on group keys invented from a design doc's Portuguese prose
 * instead of the server's real route table, and the labels did not survive
 * contact with the data.
 *
 * ⚠ INVARIANT — NOTHING MAY VANISH. Any accepted extension this module does
 * not recognise falls into `other`, never off the list. That is not defensive
 * padding: the accept list has silently shed whole families twice already (the
 * cap-table derivation hid .txt/.md/.odt/.rtf for weeks), and a display layer
 * that quietly drops a type the server accepts recreates the same bug one
 * layer up. `groupAcceptedExtensions` is total over its input, and
 * `upload-families.test.ts` pins that with the live 37-extension list.
 */
import type { DocumentUploadLimits } from "@/types/gateway";

import { acceptedExtensions, effectiveMaxBytes } from "./upload-safeguards";

export type UploadFamilyKey =
  | "documents"
  | "images"
  | "audio"
  | "video"
  | "archives"
  | "other";

export interface UploadFamily {
  key: UploadFamilyKey;
  /** Accepted extensions in this family, sorted, lowercase, dot-prefixed. */
  extensions: string[];
  /**
   * The EFFECTIVE cap shared by every member — already clamped to the
   * transport ceiling by `effectiveMaxBytes`, so it is a number the upload can
   * actually honour. Quoting the raw per-type cap here would advertise 25 MB
   * on an 18 MB transport.
   */
  capBytes: number;
  /**
   * True when members of this family do NOT share one cap. The dialog then
   * quotes the family's SMALLEST effective cap (`capBytes`), because the
   * larger one is not true of every member and a size promise that holds for
   * only some of the family is the reassuring kind of wrong.
   */
  capVaries: boolean;
}

/**
 * Extension → family. Deliberately explicit rather than pattern-matched: a
 * regex over "looks like an image" would silently reclassify a new server
 * type, whereas an unlisted extension here lands in `other` — visible, and
 * obviously in need of a line in this table.
 *
 * `office` on the server spans BOTH spreadsheets and presentations, so .xlsx
 * and .pptx shelve under documentos with the rest of the paperwork; a lawyer
 * looking for "can I send a planilha" reads the documents row.
 */
const FAMILY_BY_EXTENSION: Readonly<Record<string, UploadFamilyKey>> = {
  ".pdf": "documents",
  ".doc": "documents",
  ".docx": "documents",
  ".odt": "documents",
  ".rtf": "documents",
  ".txt": "documents",
  ".md": "documents",
  ".msg": "documents",
  ".xls": "documents",
  ".xlsx": "documents",
  ".pptx": "documents",

  ".jpg": "images",
  ".jpeg": "images",
  ".png": "images",
  ".gif": "images",
  ".bmp": "images",
  ".webp": "images",
  ".heic": "images",
  ".heif": "images",
  ".tif": "images",
  ".tiff": "images",

  ".aac": "audio",
  ".flac": "audio",
  ".m4a": "audio",
  ".mp3": "audio",
  ".oga": "audio",
  ".ogg": "audio",
  ".opus": "audio",
  ".wav": "audio",

  ".avi": "video",
  ".m4v": "video",
  ".mkv": "video",
  ".mov": "video",
  ".mp4": "video",
  ".webm": "video",

  ".zip": "archives",
  ".rar": "archives",
};

/** Presentation order. `other` last — it only appears when it has members. */
const FAMILY_ORDER: readonly UploadFamilyKey[] = [
  "documents",
  "images",
  "audio",
  "video",
  "archives",
  "other",
];

/**
 * Shelve every accepted extension into its family.
 *
 * Families with no members are omitted, so a runtime that stops accepting
 * video does not render an empty "vídeo" row. Total over its input: the
 * returned extensions are exactly `acceptedExtensions(limits)`, no more and
 * no fewer.
 */
export function groupAcceptedExtensions(
  limits: DocumentUploadLimits,
): UploadFamily[] {
  const exts = acceptedExtensions(limits);

  const buckets = new Map<UploadFamilyKey, string[]>();
  for (const ext of exts) {
    const key = FAMILY_BY_EXTENSION[ext] ?? "other";
    const bucket = buckets.get(key);
    if (bucket) bucket.push(ext);
    else buckets.set(key, [ext]);
  }

  const families: UploadFamily[] = [];
  for (const key of FAMILY_ORDER) {
    const members = buckets.get(key);
    if (!members || members.length === 0) continue;

    const caps = members.map((ext) => effectiveMaxBytes(ext, limits));
    const min = Math.min(...caps);
    const max = Math.max(...caps);

    families.push({
      key,
      extensions: [...members].sort(),
      capBytes: min,
      capVaries: min !== max,
    });
  }
  return families;
}
